import { normalizeOrganizationId, type OrganizationIdInput } from '@/lib/organization-scoping'
import queryClient from '@/lib/queryClient'
import {
  useMutationState,
  type Mutation,
  type MutationFilters,
  type MutationKey,
  type MutationScope,
  type QueryKey,
} from '@tanstack/react-query'
import type { PosCart } from './carts.types'

/**
 * The cart's writes, so its reads can't undo them. A read that a save overlapped may carry the cart from before the
 * save, and it can answer after the save's own answer is in the cache, which showed the florist the old cart again.
 * Every request that changes a cart goes through `writingCart`, and the cart's reads go through `readCart`. And a
 * POS cart's changes queue on the cart's line (`queueCartWrite`): they save one at a time, in the order staff made
 * them, and the cart shows each one meanwhile.
 */
interface CartWrites {
  /** Writes started on this cart this page session; a read compares it before and after. */
  started: number
  inFlight: number
  /** Reads waiting for the cart's writes to finish, because they have no cached cart to keep meanwhile. */
  waiting: Array<() => void>
}

// One record per cart, kept for the page session: the count of started writes must only grow while a read is out.
const cartWrites = new Map<string, CartWrites>()

function cartKeyOf(organizationId: OrganizationIdInput, cartId: number): string {
  return `${normalizeOrganizationId(organizationId) ?? ''}:${cartId}`
}

function writesOf(organizationId: OrganizationIdInput, cartId: number): CartWrites {
  const key = cartKeyOf(organizationId, cartId)
  let writes = cartWrites.get(key)
  if (writes === undefined) {
    writes = { started: 0, inFlight: 0, waiting: [] }
    cartWrites.set(key, writes)
  }

  return writes
}

/** Sends a request that changes the cart, counting it as the cart's write until it answers or fails. */
export async function writingCart<T>(
  organizationId: OrganizationIdInput,
  cartId: number,
  send: () => Promise<T>
): Promise<T> {
  const writes = writesOf(organizationId, cartId)
  writes.started += 1
  writes.inFlight += 1
  try {
    return await send()
  } finally {
    writes.inFlight -= 1
    if (writes.inFlight === 0) {
      writes.waiting.splice(0).forEach((resume) => resume())
    }
  }
}

function writesSettled(writes: CartWrites): Promise<void> {
  if (writes.inFlight === 0) {
    return Promise.resolve()
  }

  return new Promise((resume) => {
    writes.waiting.push(resume)
  })
}

/**
 * Reads the cart for its query. When a write overlapped the read, the read may be older than the write's answer, so
 * the query keeps the cart it has: the write's answer, or a refresh after it, brings the newer one. A first load has
 * nothing to keep, so it waits for the writes and reads again.
 *
 * Nothing that runs inside a write may wait on this read of the same cart, or the two would wait on each other.
 */
export async function readCart<T>(
  organizationId: OrganizationIdInput,
  cartId: number,
  queryKey: QueryKey,
  read: () => Promise<T>
): Promise<T> {
  const writes = writesOf(organizationId, cartId)
  const startedBefore = writes.started
  const overlapped = writes.inFlight > 0
  const cart = await read()
  if (!overlapped && writes.started === startedBefore) {
    return cart
  }

  const cached = queryClient.getQueryData<T>(queryKey)
  if (cached !== undefined) {
    return cached
  }
  await writesSettled(writes)

  return readCart(organizationId, cartId, queryKey, read)
}

/**
 * A cart's line is a TanStack Query mutation scope. Mutations that share a scope run one at a time, in the order they
 * were queued, and one that fails doesn't stop the next, so each write starts from the cart the write before it
 * saved. The API refuses overlapping changes to a cart, and the florist expects them in the order they made them.
 */
export function cartWriteScope(organizationId: OrganizationIdInput, cartId: number): MutationScope {
  return { id: `cart:${cartKeyOf(organizationId, cartId)}` }
}

/**
 * A change on its way to the cart. `send` writes the answer into the cache before it resolves, so the change comes off
 * in the same view that shows the answer, and a line never jumps back or counts twice. Nothing in `send` may queue on
 * the same cart: it would wait for `send` to finish, and `send` for it.
 */
export interface QueuedCartWrite<T> {
  /** One of the cart's mutation keys, so the busy and order-ending checks count the write. */
  mutationKey: MutationKey
  /** How the cart shows the write until it's saved. */
  show?: (cart: PosCart) => PosCart
  send: () => Promise<T>
}

/** Shows the write on the cart at once and sends it after the cart's earlier writes. Resolves or rejects as it does. */
export function queueCartWrite<T>(
  organizationId: OrganizationIdInput,
  cartId: number,
  { mutationKey, show, send }: QueuedCartWrite<T>
): Promise<T> {
  return queryClient
    .getMutationCache()
    .build(queryClient, {
      mutationKey,
      scope: cartWriteScope(organizationId, cartId),
      ...(show ? { meta: { showOnCart: show } } : {}),
      mutationFn: send,
    })
    .execute(undefined)
}

/** A queued write the cart shows, and the line it waits in. */
export interface ShownCartWrite {
  scopeId: string | undefined
  show: ((cart: PosCart) => PosCart) | undefined
}

const shownWriteFilters = {
  status: 'pending',
  predicate: (mutation) => mutation.options.meta?.showOnCart !== undefined,
} satisfies MutationFilters

const shownWrite = (mutation: Mutation<unknown, Error, unknown, unknown>): ShownCartWrite => ({
  scopeId: mutation.options.scope?.id,
  show: mutation.options.meta?.showOnCart,
})

/** The writes carts show, in the order they were queued. They live in the app's query client, so this reads it. */
export function useShownCartWrites(): readonly ShownCartWrite[] {
  return useMutationState({ filters: shownWriteFilters, select: shownWrite }, queryClient)
}

/** The writes carts show right now, for code outside React. */
export function queuedCartWrites(): ShownCartWrite[] {
  return queryClient.getMutationCache().findAll(shownWriteFilters).map(shownWrite)
}

/** The cart with its writes still on their way to it, in the order they were queued. */
export function withQueuedCartWrites(cart: PosCart, writes: readonly ShownCartWrite[]): PosCart {
  const { id } = cartWriteScope(cart.organization_id, cart.id)

  return writes.reduce(
    (shownCart, { scopeId, show }) => (show && scopeId === id ? show(shownCart) : shownCart),
    cart
  )
}

// Lines shown before their save answers. Saved lines have positive ids, so negative ones can't clash with them, and
// one counter for every kind of pending line keeps them from clashing with each other.
let lastTempLineId = 0

/** An id for a line the cart shows before the server has saved it. */
export function nextTempLineId(): number {
  lastTempLineId -= 1

  return lastTempLineId
}
